import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { apiGet, apiPost, apiUpload } from '../api';
import { APP_VERSION } from '../components/Layout';
import { Icon, Switch, fmtIso, useToast } from '../components/ui';

const SECTIONS = [
  { id: 's-alert', label: 'แจ้งเตือนเมื่อผิดพลาด' },
  { id: 's-mail', label: 'ส่งอีเมลเอกสาร' },
  { id: 's-ret', label: 'ระยะเก็บข้อมูล' },
  { id: 's-2fa', label: 'ยืนยันตัวตน 2 ขั้น' },
  { id: 's-pw', label: 'รหัสผ่าน' },
];

function Result({ r }) {
  if (!r) return null;
  return <span className={`inline-msg ${r.ok ? 'ok' : 'err'}`}>{r.msg}</span>;
}

export default function Settings() {
  const toast = useToast();
  const [active, setActive] = useState(SECTIONS[0].id);

  // แจ้งเตือน
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [resendApiKey, setResendApiKey] = useState('');
  const [alertFrom, setAlertFrom] = useState('');
  const [alertTo, setAlertTo] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [alertResult, setAlertResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // ส่งอีเมลเอกสาร
  const [emailFrom, setEmailFrom] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailFile, setEmailFile] = useState(null);
  const emailFileRef = useRef(null);
  const [emailResult, setEmailResult] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);

  // 2FA
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [pendingSecret, setPendingSecret] = useState(null);
  const [otpauthUri, setOtpauthUri] = useState(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [enrollError, setEnrollError] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableCode, setDisableCode] = useState('');
  const [disableError, setDisableError] = useState(null);

  // retention
  const [retentionEnabled, setRetentionEnabled] = useState(false);
  const [dailyLogsDays, setDailyLogsDays] = useState(30);
  const [cronLogLines, setCronLogLines] = useState(20000);
  const [supplierBackupDays, setSupplierBackupDays] = useState(90);
  const [lastRun, setLastRun] = useState('');
  const [lastSummary, setLastSummary] = useState('');
  const [retResult, setRetResult] = useState(null);
  const [retSaving, setRetSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiGet('/api/settings/me');
        setAlertEnabled(!!data.alert_enabled);
        setResendApiKey(data.resend_api_key || '');
        setAlertFrom(data.alert_from || '');
        setAlertTo(data.alert_to || '');
        setEmailFrom(data.email_global_from || '');
        setEmailBcc(data.email_global_bcc || '');
        setTotpEnabled(!!data.totp_enabled);
      } catch (e) { /* ignore */ }
      try {
        const r = await apiGet('/api/settings/retention');
        setRetentionEnabled(!!r.enabled);
        setDailyLogsDays(r.daily_logs_days);
        setCronLogLines(r.cron_log_lines);
        setSupplierBackupDays(r.supplier_backup_days);
        setLastRun(r.last_run || '');
        setLastSummary(r.last_summary || '');
      } catch (e) { /* ignore */ }
    })();
  }, []);

  // ไฮไลต์หัวข้อในเมนูซ้ายตามส่วนที่เลื่อนมาถึง
  useEffect(() => {
    const els = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    if (!('IntersectionObserver' in window) || !els.length) return undefined;
    const io = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActive(vis[0].target.id);
    }, { rootMargin: '-80px 0px -60% 0px' });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const flash = (setter, r, ms = 5000) => { setter(r); setTimeout(() => setter(null), ms); };

  const toggleAlert = async () => {
    try {
      const data = await apiPost('/api/alert/toggle');
      setAlertEnabled(data.enabled);
      toast(data.enabled ? 'เปิดการแจ้งเตือนแล้ว' : 'ปิดการแจ้งเตือนแล้ว');
    } catch (e) { toast('เปลี่ยนสถานะไม่สำเร็จ', 'error'); }
  };

  const saveAlert = async () => {
    setSaving(true);
    try {
      const data = await apiPost('/api/settings/email', { resend_api_key: resendApiKey, alert_from: alertFrom, alert_to: alertTo });
      flash(setAlertResult, data.ok ? { ok: true, msg: 'บันทึกแล้ว' } : { ok: false, msg: data.error || 'บันทึกไม่สำเร็จ' });
    } catch (e) { flash(setAlertResult, { ok: false, msg: 'บันทึกไม่สำเร็จ' }); } finally { setSaving(false); }
  };

  const testAlert = async () => {
    setTesting(true);
    try {
      const data = await apiPost('/api/alert/test');
      flash(setAlertResult, data.success ? { ok: true, msg: `ส่งอีเมลทดสอบถึง ${data.to} แล้ว` } : { ok: false, msg: 'ส่งไม่สำเร็จ ตรวจ API key และบันทึกก่อนทดสอบ' }, 8000);
    } catch (e) { flash(setAlertResult, { ok: false, msg: 'ส่งไม่สำเร็จ' }); } finally { setTesting(false); }
  };

  const saveEmailDefaults = async () => {
    try {
      const data = await apiPost('/api/settings/email', { email_global_from: emailFrom, email_global_bcc: emailBcc });
      flash(setEmailResult, data.ok ? { ok: true, msg: 'บันทึกผู้ส่งและ Bcc เป็นค่าเริ่มต้นแล้ว' } : { ok: false, msg: data.error || 'บันทึกไม่สำเร็จ' });
    } catch (e) { flash(setEmailResult, { ok: false, msg: 'บันทึกไม่สำเร็จ' }); }
  };

  const sendGlobalEmail = async () => {
    setEmailBusy(true);
    try {
      const form = new FormData();
      form.append('from', emailFrom);
      form.append('to', emailTo);
      form.append('bcc', emailBcc);
      form.append('subject', emailSubject);
      form.append('message', emailMessage);
      if (emailFile) form.append('file', emailFile);
      const data = await apiUpload('/api/email/send', form);
      flash(setEmailResult, data.ok ? { ok: true, msg: `ส่งถึง ${emailTo} แล้ว` } : { ok: false, msg: data.error || 'ส่งไม่สำเร็จ' }, 8000);
    } catch (e) { flash(setEmailResult, { ok: false, msg: 'ส่งไม่สำเร็จ' }); } finally { setEmailBusy(false); }
  };

  const saveRetention = async () => {
    setRetSaving(true);
    try {
      const data = await apiPost('/api/settings/retention', {
        enabled: retentionEnabled,
        daily_logs_days: Number(dailyLogsDays),
        cron_log_lines: Number(cronLogLines),
        supplier_backup_days: Number(supplierBackupDays),
      });
      flash(setRetResult, data.ok ? { ok: true, msg: 'บันทึกแล้ว' } : { ok: false, msg: data.error || 'บันทึกไม่สำเร็จ' });
    } catch (e) { flash(setRetResult, { ok: false, msg: 'บันทึกไม่สำเร็จ' }); } finally { setRetSaving(false); }
  };

  const runRetentionNow = async () => {
    setRunningNow(true);
    try {
      const data = await apiPost('/api/retention/run-now');
      setLastRun(data.summary.ran_at);
      setLastSummary(`Deleted ${data.summary.files_deleted} files (${(data.summary.bytes_freed / (1024 * 1024)).toFixed(1)} MB), trimmed cron.log by ${data.summary.cron_lines_trimmed} lines`);
      toast(`ล้างแล้ว ลบ ${data.summary.files_deleted} ไฟล์`);
    } catch (e) { toast('ล้างไม่สำเร็จ', 'error'); } finally { setRunningNow(false); }
  };

  const start2fa = async () => {
    try {
      const data = await apiPost('/api/2fa/setup');
      setPendingSecret(data.secret);
      setOtpauthUri(data.otpauth_uri);
      setEnrolling(true);
      setEnrollError(null);
      setConfirmCode('');
    } catch (e) { setEnrollError('เริ่มตั้งค่าไม่สำเร็จ ลองใหม่อีกครั้ง'); }
  };

  const confirm2fa = async () => {
    if (!pendingSecret || !confirmCode) return;
    try {
      const data = await apiPost('/api/2fa/confirm', { secret: pendingSecret, code: confirmCode });
      if (data.ok) { setEnrolling(false); setTotpEnabled(true); setBackupCodes(data.backup_codes); }
      else setEnrollError(data.error || 'รหัสไม่ถูกต้อง');
    } catch (e) { setEnrollError('ยืนยันไม่สำเร็จ'); }
  };

  const disable2fa = async () => {
    if (!disableCode) { setDisableError('กรอกรหัส 6 หลักปัจจุบันก่อน'); return; }
    try {
      const data = await apiPost('/api/2fa/disable', { code: disableCode });
      if (data.ok) { setTotpEnabled(false); setDisableCode(''); setDisableError(null); setBackupCodes(null); toast('ปิดการยืนยัน 2 ขั้นแล้ว'); }
      else setDisableError(data.error || 'รหัสไม่ถูกต้อง');
    } catch (e) { setDisableError('ปิดไม่สำเร็จ'); }
  };

  const jump = (e, id) => {
    e.preventDefault();
    setActive(id);
    document.getElementById(id)?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <>
      <div className="page-head"><div className="page-title"><h1>ตั้งค่า</h1></div></div>
      <div className="settings">
        <nav className="snav" aria-label="หัวข้อตั้งค่า">
          {SECTIONS.map((s) => <a key={s.id} href={`#${s.id}`} className={active === s.id ? 'on' : ''} onClick={(e) => jump(e, s.id)}>{s.label}</a>)}
        </nav>

        <div>
          <section className="sec" id="s-alert">
            <div className="sec-head">
              <div><h2>แจ้งเตือนเมื่อผิดพลาด</h2><p>ส่งอีเมลหาทีมเมื่อ pipeline ประมวลผลไม่สำเร็จ ผ่านบริการ Resend · บันทึกแล้วมีผลทันที ไม่ต้องรีสตาร์ต</p></div>
              <Switch checked={alertEnabled} onChange={toggleAlert} label="เปิดการแจ้งเตือน" />
            </div>
            <div className="sec-body">
              <div className="full">
                <label className="lbl" htmlFor="st-key">Resend API key</label>
                <div className="row" style={{ flexWrap: 'nowrap' }}>
                  <input className="input mono" id="st-key" type={showKey ? 'text' : 'password'} value={resendApiKey}
                    onChange={(e) => setResendApiKey(e.target.value)} placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxx" autoComplete="off" />
                  <button className="icon-btn" type="button" onClick={() => setShowKey(!showKey)} aria-label={showKey ? 'ซ่อน key' : 'แสดง key'} title={showKey ? 'ซ่อน' : 'แสดง'}><Icon name="eye" /></button>
                </div>
              </div>
              <div><label className="lbl" htmlFor="st-af">ผู้ส่ง</label><input className="input" id="st-af" value={alertFrom} onChange={(e) => setAlertFrom(e.target.value)} placeholder="noreply@ttnbcp.cloud" /></div>
              <div><label className="lbl" htmlFor="st-at">ผู้รับ</label><input className="input" id="st-at" value={alertTo} onChange={(e) => setAlertTo(e.target.value)} placeholder="you@example.com" /></div>
            </div>
            <div className="sec-foot">
              <Result r={alertResult} />
              <button className="btn btn-outline" onClick={testAlert} disabled={testing}>{testing ? 'กำลังส่ง…' : 'ส่งอีเมลทดสอบ'}</button>
              <button className="btn btn-primary" onClick={saveAlert} disabled={saving}>{saving ? 'กำลังบันทึก…' : 'บันทึก'}</button>
            </div>
          </section>

          <section className="sec" id="s-mail">
            <div className="sec-head"><div><h2>ส่งอีเมลเอกสาร</h2><p>ส่งเอกสาร (เช่น PDF ที่สร้างแล้ว) ให้ลูกค้าทางอีเมล ใช้ Resend API key เดียวกับการแจ้งเตือน · ผู้ส่งและ Bcc ที่บันทึกไว้จะเป็นค่าเริ่มต้นของปุ่ม "ส่งอีเมล" ในหน้าจัดการไฟล์ด้วย</p></div></div>
            <div className="sec-body">
              <div className="full">
                <div className="attach" role="button" tabIndex={0} onClick={() => emailFileRef.current?.click()}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') emailFileRef.current?.click(); }}>
                  <input ref={emailFileRef} type="file" hidden onChange={(e) => setEmailFile(e.target.files?.[0] || null)} />
                  <Icon name="clip" />
                  {emailFile ? (
                    <>
                      <span style={{ color: 'var(--ink)' }}>{emailFile.name} ({(emailFile.size / 1024).toFixed(1)} KB)</span>
                      <span className="spacer" />
                      <button className="icon-btn" aria-label="เอาไฟล์แนบออก" onClick={(e) => { e.stopPropagation(); setEmailFile(null); if (emailFileRef.current) emailFileRef.current.value = ''; }}><Icon name="x" /></button>
                    </>
                  ) : <span>คลิกเพื่อแนบไฟล์ (ไม่บังคับ)</span>}
                </div>
              </div>
              <div><label className="lbl" htmlFor="sm-from">ผู้ส่ง</label><input className="input" id="sm-from" value={emailFrom} onChange={(e) => setEmailFrom(e.target.value)} placeholder="noreply@ttnbcp.cloud" /></div>
              <div><label className="lbl" htmlFor="sm-to">ผู้รับ</label><input className="input" id="sm-to" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="คั่นด้วยจุลภาคถ้ามีหลายคน" /></div>
              <div><label className="lbl" htmlFor="sm-bcc">สำเนาลับ (Bcc)</label><input className="input" id="sm-bcc" value={emailBcc} onChange={(e) => setEmailBcc(e.target.value)} placeholder="ไม่บังคับ" /></div>
              <div><label className="lbl" htmlFor="sm-sub">หัวเรื่อง</label><input className="input" id="sm-sub" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} /></div>
              <div className="full"><label className="lbl" htmlFor="sm-msg">ข้อความ</label><textarea className="input" id="sm-msg" rows={5} value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} /></div>
            </div>
            <div className="sec-foot">
              <Result r={emailResult} />
              <button className="btn btn-outline" onClick={saveEmailDefaults}>บันทึกผู้ส่ง/Bcc เป็นค่าเริ่มต้น</button>
              <button className="btn btn-primary" onClick={sendGlobalEmail} disabled={emailBusy || !emailFrom || !emailTo}><Icon name="mail" />{emailBusy ? 'กำลังส่ง…' : 'ส่งอีเมล'}</button>
            </div>
          </section>

          <section className="sec" id="s-ret">
            <div className="sec-head">
              <div><h2>ระยะเก็บข้อมูล</h2><p>ลบ log และไฟล์ต้นฉบับเก่าวันละครั้งอัตโนมัติ ไฟล์ที่ลบไปแล้วยังกู้ได้จาก backup บน OCI อีก 30 วัน</p></div>
              <Switch checked={retentionEnabled} onChange={setRetentionEnabled} label="เปิดการลบอัตโนมัติ" />
            </div>
            <div className="sec-body">
              <div><label className="lbl" htmlFor="rt-1">log รายวัน (main, PDF-REPORT, ทุก TUN)</label><div className="inline-num"><input className="input" id="rt-1" type="number" min={1} value={dailyLogsDays} onChange={(e) => setDailyLogsDays(e.target.value)} />วัน</div></div>
              <div><label className="lbl" htmlFor="rt-3">Backup/PDFback ทุก TUN</label><div className="inline-num"><input className="input" id="rt-3" type="number" min={1} value={supplierBackupDays} onChange={(e) => setSupplierBackupDays(e.target.value)} />วัน</div></div>
              <div><label className="lbl" htmlFor="rt-2">cron.log</label><div className="inline-num"><input className="input" id="rt-2" type="number" min={100} value={cronLogLines} onChange={(e) => setCronLogLines(e.target.value)} />บรรทัดล่าสุด</div></div>
              <div>
                <span className="lbl">ล้างล่าสุด</span>
                <div className="status-line" style={{ fontSize: 13.5 }}>{lastRun ? <>{fmtIso(lastRun)} · <span className="muted">{lastSummary}</span></> : <span className="muted">ยังไม่เคยล้าง</span>}</div>
              </div>
              <div className="full hint">กดบันทึกเพื่อให้การเปิด/ปิดและตัวเลขมีผล</div>
            </div>
            <div className="sec-foot">
              <Result r={retResult} />
              <button className="btn btn-outline" onClick={runRetentionNow} disabled={runningNow}>{runningNow ? 'กำลังล้าง…' : 'ล้างเดี๋ยวนี้'}</button>
              <button className="btn btn-primary" onClick={saveRetention} disabled={retSaving}>{retSaving ? 'กำลังบันทึก…' : 'บันทึก'}</button>
            </div>
          </section>

          <section className="sec" id="s-2fa">
            <div className="sec-head"><div><h2>ยืนยันตัวตน 2 ขั้น</h2><p>ใช้รหัส 6 หลักจากแอป Authenticator (Google Authenticator, Authy ฯลฯ) ทุกครั้งที่เข้าสู่ระบบ</p></div></div>
            <div className="sec-body">
              {!enrolling && (
                <div className="full status-line">
                  {totpEnabled ? <span className="pill ok">เปิดใช้อยู่</span> : <span className="pill off">ยังไม่เปิด</span>}
                  {!totpEnabled && <button className="btn btn-primary btn-sm" onClick={start2fa}>เปิดใช้</button>}
                </div>
              )}
              {totpEnabled && !enrolling && (
                <div className="full">
                  <label className="lbl" htmlFor="tf-dis">ปิดการยืนยัน 2 ขั้น</label>
                  <div className="row">
                    <input className="input mono" id="tf-dis" style={{ width: 180, textAlign: 'center' }} inputMode="numeric" maxLength={8}
                      placeholder="รหัส 6 หลักปัจจุบัน" value={disableCode} onChange={(e) => setDisableCode(e.target.value)} />
                    <button className="btn btn-danger" onClick={disable2fa}>ปิด</button>
                  </div>
                  {disableError && <div className="inline-msg err" style={{ marginTop: 6 }}>{disableError}</div>}
                </div>
              )}
              {enrolling && (
                <div className="full">
                  <ol className="steps">
                    <li>สแกน QR ด้วยแอป Authenticator</li>
                    <li>หรือพิมพ์รหัสลับเอง: <span className="code">{pendingSecret}</span></li>
                    <li>กรอกรหัส 6 หลักที่แอปแสดง แล้วกดยืนยัน</li>
                  </ol>
                  <div style={{ margin: '14px 0' }}>{otpauthUri && <span className="qr"><QRCodeSVG value={otpauthUri} size={200} /></span>}</div>
                  <div className="row">
                    <input className="input mono" style={{ width: 140, textAlign: 'center', letterSpacing: '.2em' }} inputMode="numeric" maxLength={8}
                      placeholder="000000" value={confirmCode} onChange={(e) => setConfirmCode(e.target.value)} aria-label="รหัสยืนยัน" />
                    <button className="btn btn-primary" onClick={confirm2fa}>ยืนยัน</button>
                    <button className="btn-ghost" onClick={() => setEnrolling(false)}>ยกเลิก</button>
                  </div>
                  {enrollError && <div className="inline-msg err" style={{ marginTop: 6 }}>{enrollError}</div>}
                </div>
              )}
              {enrollError && !enrolling && <div className="full inline-msg err">{enrollError}</div>}
              {backupCodes && (
                <div className="full">
                  <div className="alert alert-warn" style={{ marginBottom: 10 }}>
                    <b>เก็บรหัสสำรองนี้ไว้ในที่ปลอดภัย</b> ใช้แทนรหัสจากแอปได้รหัสละครั้งเวลาไม่มีมือถือ หน้านี้จะไม่แสดงรหัสอีก
                  </div>
                  <div className="backup-codes">{backupCodes.map((c) => <span key={c}>{c}</span>)}</div>
                </div>
              )}
            </div>
          </section>

          <section className="sec" id="s-pw">
            <div className="sec-head"><div><h2>รหัสผ่าน</h2><p>รหัสผ่านเก็บเป็น env ใน Coolify จึงเปลี่ยนจากหน้านี้ไม่ได้</p></div></div>
            <div className="sec-body">
              <div className="full">
                <ol className="steps">
                  <li>สร้าง hash: <span className="code">python -c "import auth; print(auth.hash_password('...'))"</span></li>
                  <li>ใส่ค่าใน <span className="code">ADMIN_PASSWORD</span> ที่ Coolify → service dashboard → Environment Variables</li>
                  <li>Redeploy service (ตรวจ mount ใน compose ก่อนตามขั้นตอนใน CLAUDE.md)</li>
                </ol>
              </div>
            </div>
          </section>

          <div className="page-foot">TTNBCP Dashboard v{APP_VERSION}</div>
        </div>
      </div>
    </>
  );
}
